import React, { useRef } from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { MIN_WIDGET_FRACTION, ScreenWidget, clampWidget } from '../../../../components/ScreenComposer/composer.types';
import { capturePointer } from '../../../../components/ReelMaker/pointerCapture';

type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const ALL_HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const TICKER_HANDLES: Handle[] = ['n', 's'];

const CURSOR: Record<Handle, string> = {
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    nw: 'nwse-resize',
    se: 'nwse-resize',
};

export interface ComposerWidgetFrameProps {
    widget: ScreenWidget;
    /** Screen size in pixels (the layout's design size). */
    designWidth: number;
    designHeight: number;
    /** Current zoom of the stage. */
    scale: number;
    zIndex: number;
    selected: boolean;
    readOnly?: boolean;
    onSelect: (id: string) => void;
    /** `commit` is true once a drag ends (marks the layout as changed). */
    onChange: (next: ScreenWidget, commit: boolean) => void;
    onOpen: (id: string) => void;
    children: React.ReactNode;
}

/** New geometry for a resize from `handle`, keeping the opposite edges where they were. */
const resizeFrom = (base: ScreenWidget, handle: Handle, dx: number, dy: number): ScreenWidget => {
    let { x, y, width, height } = base;
    if (handle.includes('e')) width = Math.min(1 - x, Math.max(MIN_WIDGET_FRACTION, base.width + dx));
    if (handle.includes('s')) height = Math.min(1 - y, Math.max(MIN_WIDGET_FRACTION, base.height + dy));
    if (handle.includes('w')) {
        const right = base.x + base.width;
        x = Math.min(right - MIN_WIDGET_FRACTION, Math.max(0, base.x + dx));
        width = right - x;
    }
    if (handle.includes('n')) {
        const bottom = base.y + base.height;
        y = Math.min(bottom - MIN_WIDGET_FRACTION, Math.max(0, base.y + dy));
        height = bottom - y;
    }
    return clampWidget({ ...base, x, y, width, height });
};

/**
 * One widget on the Composer stage: select by click, move by dragging the body, resize from any of
 * the eight handles (a News Ticker only moves and resizes vertically). Double-click opens settings.
 */
export const ComposerWidgetFrame: React.FC<ComposerWidgetFrameProps> = ({
    widget,
    designWidth,
    designHeight,
    scale,
    zIndex,
    selected,
    readOnly = false,
    onSelect,
    onChange,
    onOpen,
    children,
}) => {
    const theme = useTheme();
    const session = useRef<{ kind: 'move' | Handle; startX: number; startY: number; base: ScreenWidget; moved: boolean; last: ScreenWidget } | null>(null);
    const isTicker = widget.type === 'news-ticker' || widget.type === 'stock-ticker';
    const movable = !readOnly && !widget.locked;

    const begin = (kind: 'move' | Handle) => (e: React.PointerEvent) => {
        if (readOnly || e.button !== 0) return;
        e.stopPropagation();
        onSelect(widget.id);
        if (!movable) return;
        e.preventDefault();
        session.current = { kind, startX: e.clientX, startY: e.clientY, base: widget, moved: false, last: widget };
        capturePointer(e);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const s = session.current;
        if (!s) return;
        const dx = (e.clientX - s.startX) / scale / designWidth;
        const dy = (e.clientY - s.startY) / scale / designHeight;
        if (!s.moved && Math.abs(e.clientX - s.startX) + Math.abs(e.clientY - s.startY) < 3) return;
        s.moved = true;
        const next =
            s.kind === 'move'
                ? clampWidget({ ...s.base, x: isTicker ? 0 : s.base.x + dx, y: s.base.y + dy })
                : resizeFrom(s.base, s.kind, isTicker ? 0 : dx, dy);
        s.last = next;
        onChange(next, false);
    };

    const end = (e: React.PointerEvent) => {
        const s = session.current;
        session.current = null;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        if (s?.moved) onChange(s.last, true);
    };

    const handlePx = 12 / scale;
    const outline = 2 / scale;
    const handles = !movable || !selected ? [] : isTicker ? TICKER_HANDLES : ALL_HANDLES;

    const handlePosition = (h: Handle): React.CSSProperties => {
        const half = -handlePx / 2;
        const style: React.CSSProperties = { width: handlePx, height: handlePx };
        if (h.includes('n')) style.top = half;
        else if (h.includes('s')) style.bottom = half;
        else style.top = `calc(50% - ${handlePx / 2}px)`;
        if (h.includes('w')) style.left = half;
        else if (h.includes('e')) style.right = half;
        else style.left = `calc(50% - ${handlePx / 2}px)`;
        return style;
    };

    return (
        <Box
            role="group"
            aria-label={`${widget.name} widget`}
            onPointerDown={begin('move')}
            onPointerMove={onPointerMove}
            onPointerUp={end}
            onPointerCancel={end}
            onDoubleClick={(e) => {
                if (readOnly) return;
                e.stopPropagation();
                onOpen(widget.id);
            }}
            sx={{
                position: 'absolute',
                left: widget.x * designWidth,
                top: widget.y * designHeight,
                width: widget.width * designWidth,
                height: widget.height * designHeight,
                zIndex,
                cursor: readOnly ? 'default' : movable ? (isTicker ? 'ns-resize' : 'move') : 'pointer',
                opacity: widget.visible ? 1 : 0.35,
                outline: selected ? `${outline}px solid ${theme.palette.secondary.main}` : `${outline / 2}px dashed rgba(255,255,255,0.35)`,
                outlineOffset: 0,
                touchAction: 'none',
                userSelect: 'none',
            }}
        >
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>{children}</Box>
            {handles.map((h) => (
                <Box
                    key={h}
                    aria-label={`Resize ${widget.name} ${h}`}
                    onPointerDown={begin(h)}
                    onPointerMove={onPointerMove}
                    onPointerUp={end}
                    onPointerCancel={end}
                    style={handlePosition(h)}
                    sx={{
                        position: 'absolute',
                        zIndex: 2,
                        cursor: CURSOR[h],
                        backgroundColor: theme.palette.background.paper,
                        border: `${outline}px solid ${theme.palette.secondary.main}`,
                        borderRadius: '2px',
                        boxSizing: 'border-box',
                    }}
                />
            ))}
            {selected && !readOnly && (
                <Box sx={{ position: 'absolute', top: 6 / scale, right: 6 / scale, zIndex: 3, transform: `scale(${1 / scale})`, transformOrigin: 'top right' }}>
                    <Tooltip title={`${widget.name} settings`}>
                        <IconButton
                            size="small"
                            aria-label={`${widget.name} settings`}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpen(widget.id);
                            }}
                            sx={{ backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText, '&:hover': { backgroundColor: theme.palette.secondary.dark } }}
                        >
                            <TuneIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default ComposerWidgetFrame;
